import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import PlatefullAILogo from '../../../images/PlatefullAILogoNB.svg';
import ToggleSwitch from '../../../components/ToggleSwitch/ToggleSwitch';
import StepProgress from '../../../components/StepProgress/StepProgress';
import SignOutLink from '../../../components/SignOutLink/SignOutLink';
import styles from '../Onboard.module.css';

// Dietary preferences are now an array of strings
type DietaryPreferenceType = 'vegan' | 'vegetarian' | 'pescatarian' | 'gluten_free' | 'dairy_free' | 'nut_free' | 'egg_free' | 'soy_free' | 'shellfish_free' | 'fish_free' | 'sesame_free' | 'low_fodmap' | 'keto' | 'paleo' | 'atkins' | 'corn_free' | 'mustard_free';

interface DietaryStepProps {
  onNext: (data: any) => void;
  onSkip: () => void;
  onSkipAll: () => void;
  isLoading: boolean;
  error: string;
  user: any;
  onStepClick?: (stepNumber: number) => void;
}

const DietaryStep: React.FC<DietaryStepProps> = ({ onNext, onSkip, onSkipAll, isLoading, error, user, onStepClick }) => {
  const { saveUserPreferences, saveUserPreferencesToStorage } = useAuth();
  
  // Use user preferences directly - dietary is now an array of strings
  const dietaryPreferences = user?.preferences?.dietary || [];

  // Dietary hierarchy system
  const dietaryHierarchy = {
    vegan: {
      conflicts: ['vegetarian', 'pescatarian', 'dairy_free', 'fish_free', 'shellfish_free', 'egg_free', 'paleo'],
      includes: ['gluten_free', 'nut_free', 'soy_free', 'sesame_free', 'corn_free', 'mustard_free', 'low_fodmap']
    },
    vegetarian: {
      conflicts: ['pescatarian', 'fish_free', 'shellfish_free'],
      includes: ['dairy_free', 'egg_free', 'gluten_free', 'nut_free', 'soy_free', 'sesame_free', 'corn_free', 'mustard_free', 'low_fodmap']
    },
    pescatarian: {
      conflicts: ['fish_free', 'shellfish_free'],
      includes: ['dairy_free', 'egg_free', 'gluten_free', 'nut_free', 'soy_free', 'sesame_free', 'corn_free', 'mustard_free', 'low_fodmap']
    }
  };

  const dietaryOptions = [
    { key: 'vegan', label: 'Vegan', explanation: 'No meat, no fish, no dairy, no eggs, no honey, no animal products' },
    { key: 'vegetarian', label: 'Vegetarian', explanation: 'No meat, no fish, but dairy and eggs are allowed' },
    { key: 'pescatarian', label: 'Pescatarian', explanation: 'No meat, but fish and seafood are allowed' },
    { key: 'gluten_free', label: 'Gluten Allergy', explanation: 'No wheat, barley, rye, or gluten-containing ingredients' },
    { key: 'dairy_free', label: 'Dairy Allergy', explanation: 'No milk, cheese, butter, or dairy products' },
    { key: 'nut_free', label: 'Nut Allergy', explanation: 'No tree nuts, peanuts, or nut products' },
    { key: 'egg_free', label: 'Egg Allergy', explanation: 'No eggs or egg-containing products' },
    { key: 'soy_free', label: 'Soy Allergy', explanation: 'No soybeans, tofu, soy sauce, or soy products' },
    { key: 'shellfish_free', label: 'Shellfish Allergy', explanation: 'No shrimp, crab, lobster, or shellfish' },
    { key: 'fish_free', label: 'Fish Allergy', explanation: 'No fish or fish products' },
    { key: 'sesame_free', label: 'Sesame Allergy', explanation: 'No sesame seeds, tahini, or sesame products' },
    { key: 'low_fodmap', label: 'Low FODMAP', explanation: 'Reduces fermentable carbohydrates for digestive health' },
    { key: 'keto', label: 'Keto', explanation: 'Very low carb, high fat diet for ketosis' },
    { key: 'paleo', label: 'Paleo', explanation: 'No processed foods, grains, or dairy' },
    { key: 'atkins', label: 'Atkins', explanation: 'Low carb diet with phases of carb restriction' },
    { key: 'corn_free', label: 'Corn Allergy', explanation: 'No corn, corn syrup, or corn products' },
    { key: 'mustard_free', label: 'Mustard Allergy', explanation: 'No mustard seeds, mustard powder, or mustard products' },
  ];

  // Check if an option should be disabled due to conflicts
  const isOptionDisabled = (optionKey: string): boolean => {
    for (const selectedPreference of dietaryPreferences) {
      if (dietaryHierarchy[selectedPreference as keyof typeof dietaryHierarchy]?.conflicts?.includes(optionKey)) {
        return true;
      }
    }
    return false;
  };

  // Get the relationship status for an option
  const getOptionStatus = (optionKey: string): 'conflict' | 'neutral' => {
    for (const selectedPreference of dietaryPreferences) {
      if (dietaryHierarchy[selectedPreference as keyof typeof dietaryHierarchy]?.conflicts?.includes(optionKey)) {
        return 'conflict';
      }
    }
    return 'neutral';
  };

  const handlePreferenceChange = (key: DietaryPreferenceType) => {
    const isCurrentlySelected = dietaryPreferences.includes(key);
    const currentCount = dietaryPreferences.length;
    
    // If trying to select and already at max (3), don't allow
    if (!isCurrentlySelected && currentCount >= 3) {
      return;
    }
    
    // If trying to select a conflicting option, don't allow
    if (!isCurrentlySelected && isOptionDisabled(key)) {
      return;
    }
    
    // Create new preferences array
    let newPreferences = isCurrentlySelected
      ? dietaryPreferences.filter((pref: string) => pref !== key)
      : [...dietaryPreferences, key];
    
    // If selecting a parent diet, remove all conflicting children
    if (!isCurrentlySelected && dietaryHierarchy[key as keyof typeof dietaryHierarchy]) {
      const conflicts = dietaryHierarchy[key as keyof typeof dietaryHierarchy]?.conflicts || [];
      newPreferences = newPreferences.filter((pref: string) => !conflicts.includes(pref));
    }
    
    // Save to localStorage for persistence
    saveUserPreferencesToStorage({
      dietary: newPreferences,
      healthConcerns: user?.preferences?.healthConcerns || [],
      ingredientPreferences: user?.preferences?.ingredientPreferences || []
    });
  };

  const handleNext = async () => {
    if (isLoading) {
      return;
    }
    
    try {
      await saveUserPreferences(dietaryPreferences);
      onNext({});
    } catch (err) {
      console.error('Error saving dietary preferences:', err);
    }
  };

  const handleSkipAll = async () => {
    if (isLoading) {
      return;
    }
    
    try {
      await saveUserPreferences(dietaryPreferences);
      onSkipAll();
    } catch (err) {
      console.error('Error saving dietary preferences before skip all:', err);
    }
  };

  const selectedCount = dietaryPreferences.length;
  const isAtMax = selectedCount >= 3;
  

  return (
    <div className={styles.stepContainer}>
      <div className={styles.stepHeader}>
        <div className={styles.logoContainer}>
          <img src={PlatefullAILogo} alt="PLATEFULL AI Logo" className={styles.logoImage} />
        </div>
        <h2 className={styles.stepTitle}>
          Dietary
        </h2>
      </div>
      
      <StepProgress 
        currentStep={2} 
        totalSteps={4} 
        stepNames={['Account', 'Dietary', 'Health', 'Favorites']}
        onStepClick={onStepClick}
      />
      
      <div className={styles.stepDescription}>
        <div className={styles.stepDescriptionText}>
          <p>
            Select up to 3 dietary preferences and restrictions
          </p>
        </div>
      </div>

      <div className={styles.stepContent}>
        <div className={styles.preferencesGrid}>
          {dietaryOptions.map(({ key, label, explanation }) => {
            const isChecked = dietaryPreferences.includes(key as DietaryPreferenceType);
            const isDisabled = isLoading || (isAtMax && !isChecked) || isOptionDisabled(key);
            const status = getOptionStatus(key);
            
            return (
              <ToggleSwitch
                key={key}
                id={`onboard-dietary-${key}`}
                checked={isChecked}
                onChange={() => handlePreferenceChange(key as DietaryPreferenceType)}
                disabled={isDisabled}
                label={label}
                explanation={explanation}
                statusIndicator={status === 'conflict' ? {
                  text: 'Vegan conflict',
                  type: 'conflict'
                } : undefined}
              />
            );
          })}
        </div>

        {error && (
          <div className={styles.errorMessage}>
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}
      </div>

      <div className={styles.buttonContainer}>
        <button
          type="button"
          onClick={handleSkipAll}
          className={styles.secondaryButton}
          disabled={isLoading}
        >
          <span>
            {isLoading && <span className={styles.loadingSpinner}></span>}
            {isLoading ? 'Saving...' : 'Skip All'}
          </span>
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={isLoading}
          className={styles.primaryButton}
        >
          <span>
            {isLoading && <span className={styles.loadingSpinner}></span>}
            {isLoading ? 'Saving...' : 'Next'}
          </span>
        </button>
      </div>
      
      <SignOutLink />
    </div>
  );
};

export default DietaryStep;
